/**
 * CR-6348: Company section of the reworked SO Lines filter panel.
 * Fields: Country -> Division -> Site -> Warehouse, with the cascading
 * behaviour the ticket requires (Division -> Site restriction preserved).
 */
import type { LucideIcon } from 'lucide-react';
import SearchableSelect from '../../../../../ui/SearchableSelect';
import { POLinesFilters, FilterOptions } from '../../types';
import { FilterSection, FIELD_CLS, LABEL_CLS } from '../fieldStyles';
import { useFilterVisibility } from '../../../../../../hooks/useFilterVisibility';

interface CompanySectionProps {
  filters: POLinesFilters;
  onFilterChange: (key: string, value: string) => void;
  siteCountries: string[];
  divisions: NonNullable<FilterOptions['divisions']>;
  companySiteOptions: FilterOptions['customers'];
  warehouseOptions: NonNullable<FilterOptions['warehouses']>;
  // Cascade handlers clear now-invalid dependent selections.
  onCompanyCountryChange: (value: string) => void;
  onDivisionChange: (value: string) => void;
  onCompanySiteChange: (value: string) => void;
  /** CR-6347: Optional icon for the section header */
  icon?: LucideIcon;
}

export function CompanySection({
  filters,
  onFilterChange,
  siteCountries,
  divisions,
  companySiteOptions,
  warehouseOptions,
  onCompanyCountryChange,
  onDivisionChange,
  onCompanySiteChange,
  icon,
}: CompanySectionProps) {
  // CR-4802: filters this tenant shows — one answer for every panel.
  const fv = useFilterVisibility();
  return (
    <FilterSection title="Company" icon={icon}>
      {/* Country — CR-4802: only when the tenant spans countries */}
      {fv.companyCountry && (
      <div>
        <label className={LABEL_CLS}>Country</label>
        <select
          value={filters.companyCountry}
          onChange={(e) => onCompanyCountryChange(e.target.value)}
          className={FIELD_CLS}
        >
          <option value="">All Countries</option>
          {siteCountries.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>
      )}

      {/* Division — CR-4802: only when the hierarchy has a division layer */}
      {fv.division && (
      <div>
        <label className={LABEL_CLS}>Division</label>
        <select
          value={filters.division}
          onChange={(e) => onDivisionChange(e.target.value)}
          className={FIELD_CLS}
        >
          <option value="">All Divisions</option>
          {divisions.map((d) => (
            <option key={d.id} value={String(d.id)}>{d.name}</option>
          ))}
        </select>
      </div>
      )}

      {/* Site (cascaded by Country + Division) */}
      <div>
        <label className={LABEL_CLS}>Site</label>
        <SearchableSelect
          options={companySiteOptions.map((c) => ({ value: String(c.id), label: c.name }))}
          value={filters.customer}
          onChange={onCompanySiteChange}
          placeholder="All Company Sites"
        />
      </div>

      {/* Warehouse (cascaded by Site) — CR-4802: only when warehouse data is pulled in */}
      {fv.warehouse && (
      <div>
        <label className={LABEL_CLS}>Warehouse</label>
        <select
          value={filters.warehouse}
          onChange={(e) => onFilterChange('warehouse', e.target.value)}
          className={FIELD_CLS}
        >
          <option value="">All Warehouses</option>
          {warehouseOptions.map((w) => (
            <option key={w.id} value={String(w.id)}>{w.name}</option>
          ))}
        </select>
      </div>
      )}
    </FilterSection>
  );
}
